import { useMemo } from 'react'
import dayjs from 'dayjs'
import { KmsVehicle, Services } from '../../../../../../store/slices/timesheet/timesheetPageSlice'

interface ServiceState {
	startTime: string
	startDate: string
	endTime: string
	endDate: string
	passengers: number
}

export const useServiceCalculations = (serviceDetail: Services, state: ServiceState, kmsVehicles: KmsVehicle[]) => {
	const MINIMUM_DELAY_TIME_IN_MINUTS = 10
	const MAXIMUM_OVERTIME_IN_MINUTS = 60

	const isReserveService = useMemo(() => serviceDetail.service.isReserve ?? false, [serviceDetail.service.isReserve])
	const totalServiceDays = useMemo(
		() => serviceDetail.service.continuedServiceTotal,
		[serviceDetail.service.continuedServiceTotal],
	)
	const currentServiceDay = useMemo(
		() => serviceDetail.service.continuedServicePosition,
		[serviceDetail.service.continuedServicePosition],
	)
	const continuedServiceStartDate = useMemo(
		() => serviceDetail.service.continuedServiceStartDate,
		[serviceDetail.service.continuedServiceStartDate],
	)
	const continuedServiceEndDate = useMemo(
		() => serviceDetail.service.continuedServiceEndDate,
		[serviceDetail.service.continuedServiceEndDate],
	)
	const isTowDriversService = useMemo(
		() => serviceDetail.mainSecundaryDriver !== null,
		[serviceDetail.mainSecundaryDriver],
	)
	const isSecondaryDriverService = useMemo(
		() => serviceDetail.service.seconddriver ?? false,
		[serviceDetail.service.seconddriver],
	)
	const isContinuousService = useMemo(() => continuedServiceEndDate !== null, [continuedServiceEndDate])

	const hasNotes: boolean = useMemo(
		() =>
			!!(
				(serviceDetail.serviceExecution?.comments && serviceDetail.serviceExecution.comments.length > 0) ||
				(serviceDetail.serviceExecution?.startTimeErrorComment &&
					serviceDetail.serviceExecution.startTimeErrorComment.length > 0) ||
				(serviceDetail.serviceExecution?.endTimeErrorComment &&
					serviceDetail.serviceExecution.endTimeErrorComment.length > 0) ||
				(serviceDetail.serviceExecution?.paxErrorComment && serviceDetail.serviceExecution.paxErrorComment.length > 0)
			),
		[serviceDetail.serviceExecution],
	)

	const startTimeMatches: boolean = useMemo(() => {
		const scheduledStart = dayjs(serviceDetail.service?.startDateTime)
		const executedStart = dayjs(`${state.startDate}T${state.startTime}`)
		return executedStart.diff(scheduledStart, 'minute') >= MINIMUM_DELAY_TIME_IN_MINUTS
	}, [serviceDetail.service?.startDateTime, state.startDate, state.startTime, MINIMUM_DELAY_TIME_IN_MINUTS])

	const executedTotalKmsInEmpty = useMemo(
		() =>
			kmsVehicles
				?.filter(kmv => kmv.inEmpty)
				.reduce((total, kmv) => total + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0) ?? 0,
		[kmsVehicles],
	)
	const executedTotalKmsInNotEmpty = useMemo(
		() =>
			kmsVehicles
				?.filter(kmv => !kmv.inEmpty)
				.reduce((total, kmv) => total + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0) ?? 0,
		[kmsVehicles],
	)
	const scheduledTotalKmsInEmpty = useMemo(
		() =>
			serviceDetail.service?.kmsVehicles
				?.filter(kmv => kmv.inEmpty)
				.reduce((total, kmv) => total + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0) ?? 0,
		[serviceDetail.service?.kmsVehicles],
	)
	const scheduledTotalKmsInNotEmpty = useMemo(
		() =>
			serviceDetail.service?.kmsVehicles
				?.filter(kmv => !kmv.inEmpty)
				.reduce((total, kmv) => total + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0) ?? 0,
		[serviceDetail.service?.kmsVehicles],
	)
	const executedTotalKms = useMemo(
		() => executedTotalKmsInEmpty + executedTotalKmsInNotEmpty,
		[executedTotalKmsInEmpty, executedTotalKmsInNotEmpty],
	)
	const scheduledTotalKms = useMemo(
		() => scheduledTotalKmsInEmpty + scheduledTotalKmsInNotEmpty,
		[scheduledTotalKmsInEmpty, scheduledTotalKmsInNotEmpty],
	)

	const scheduledPassengers = useMemo(() => serviceDetail.service?.pax ?? 0, [serviceDetail.service?.pax])
	const transportedPassengers = useMemo(
		() => serviceDetail.serviceExecution?.pax ?? 0,
		[serviceDetail.serviceExecution?.pax],
	)

	const showEndTimeTooltip = useMemo(() => {
		const scheduledEnd = dayjs(serviceDetail.service?.endDateTime)
		const executedEnd = dayjs(`${state.endDate}T${state.endTime}`)
		return executedEnd.diff(scheduledEnd, 'minute') >= MAXIMUM_OVERTIME_IN_MINUTS
	}, [serviceDetail.service?.endDateTime, state.endDate, state.endTime, MAXIMUM_OVERTIME_IN_MINUTS])

	const showTotalKmsTooltip = useMemo(
		() => !!(executedTotalKms !== scheduledTotalKms),
		[executedTotalKms, scheduledTotalKms],
	)
	const showTotalKmsInNotEmptyTooltip = useMemo(
		() => !!(executedTotalKmsInNotEmpty !== scheduledTotalKmsInNotEmpty),
		[executedTotalKmsInNotEmpty, scheduledTotalKmsInNotEmpty],
	)
	const showTotalKmsInEmptyTooltip = useMemo(
		() => !!(executedTotalKmsInEmpty !== scheduledTotalKmsInEmpty),
		[executedTotalKmsInEmpty, scheduledTotalKmsInEmpty],
	)
	const showPassengersTooltip = useMemo(
		() => state.passengers > scheduledPassengers || transportedPassengers > scheduledPassengers,
		[transportedPassengers, scheduledPassengers, state.passengers],
	)

	return {
		isReserveService,
		totalServiceDays,
		currentServiceDay,
		continuedServiceStartDate,
		continuedServiceEndDate,
		isTowDriversService,
		isSecondaryDriverService,
		isContinuousService,
		hasNotes,
		startTimeMatches,
		executedTotalKms,
		executedTotalKmsInEmpty,
		executedTotalKmsInNotEmpty,
		scheduledTotalKms,
		scheduledTotalKmsInEmpty,
		scheduledTotalKmsInNotEmpty,
		scheduledPassengers,
		showEndTimeTooltip,
		showTotalKmsTooltip,
		showTotalKmsInNotEmptyTooltip,
		showTotalKmsInEmptyTooltip,
		showPassengersTooltip,
	}
}
